import { useEffect, useState } from "react";
import { SurveyOption } from "../../surveys.types";
import IconGripHorizontal from "@/assets/icons/grip-horizontal.svg?react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";

type Props = {
  options: SurveyOption[];
  value?: string;
  onChange: (ids: string) => void;
};

const SortableItem = ({
  item,
  isDragging,
}: {
  item: SurveyOption;
  isDragging: boolean;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isSorting } =
    useSortable({
      id: item.id,
      transition: {
        duration: 200,
        easing: "cubic-bezier(0.25, 1, 0.5, 1)",
      },
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isSorting ? transition : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`w-full ${isDragging ? "opacity-30" : ""}`}
    >
      <div className="flex items-center justify-between gap-3 py-3 px-3 w-full rounded-xl bg-gray-4 border border-white/[0.04]">
        <span className="text-sm font-medium text-white text-start leading-130">
          {item.text}
        </span>
        <div
          className="flex items-center shrink-0 touch-none cursor-grab active:cursor-grabbing text-white/30"
          {...attributes}
          {...listeners}
        >
          <IconGripHorizontal />
        </div>
      </div>
    </div>
  );
};

const DragItem = ({ item }: { item: SurveyOption }) => (
  <div className="w-full">
    <div className="flex items-center justify-between gap-3 py-3 px-3 w-full rounded-xl bg-gray-4 border border-blue/30 shadow-[0_7px_33px_-18px_rgba(59,130,246,0.5)] scale-[1.02]">
      <span className="text-sm font-medium text-white text-start leading-130">
        {item.text}
      </span>
      <div className="flex items-center shrink-0 text-white/30">
        <IconGripHorizontal />
      </div>
    </div>
  </div>
);

export default function Ordering({ options, value, onChange }: Props) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const currentOrder = value ? value.split(",") : options.map((o) => o.id);

  // Surveys aren't graded — the displayed order is itself a valid ranking.
  // Emit the default order once so an un-touched ORDERING question still
  // counts as answered and gets persisted.
  useEffect(() => {
    if (!value && options.length > 0) {
      onChange(options.map((o) => o.id).join(","));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const optionsMap = options.reduce(
    (acc, opt) => {
      acc[opt.id] = opt;
      return acc;
    },
    {} as Record<string, SurveyOption>
  );

  const orderedOptions = currentOrder.map((id) => optionsMap[id]).filter(Boolean);
  const activeItem = activeId ? optionsMap[activeId] : null;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 3 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 80, tolerance: 5 } })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;

    const oldIndex = currentOrder.indexOf(String(active.id));
    const newIndex = currentOrder.indexOf(String(over.id));
    const newOrder = arrayMove(currentOrder, oldIndex, newIndex);
    onChange(newOrder.join(","));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={currentOrder} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col items-center w-full justify-center gap-2">
          {orderedOptions.map((item) => (
            <SortableItem
              key={item.id}
              item={item}
              isDragging={activeId === item.id}
            />
          ))}
        </div>
      </SortableContext>

      <DragOverlay
        dropAnimation={{
          duration: 200,
          easing: "cubic-bezier(0.25, 1, 0.5, 1)",
        }}
      >
        {activeItem ? <DragItem item={activeItem} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
